import * as React from 'react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  FindWorkflowDocument,
  LoadFormDocument,
  NewRequestPageDocument,
  ProcurementTypePublishChangesDocument,
  ProcurementTypePublishDocument,
  RequestTypeDetailDocument,
  SaveProcurementTypeDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useLoadForm } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestTypeDetailHeader } from 'modules/procurement/components/RequestTypeDetailHeader'
import PublishTypeModal from 'modules/procurement/components/procurementTypes/PublishTypeModal'
import SaveRequestTypeModal from 'modules/procurement/components/procurementTypes/SaveRequestTypeModal'
import { NewPurchaseRequestModal } from 'modules/procurement/components/purchaseRequests/NewRequestModal'
import TabContent from 'modules/procurement/pages/RequestTypeDetailPage/TabContent'
import { WorkflowProvider, useWorkflow } from 'modules/workflows/contexts/WorkflowProvider'
import { getValidationErrorSummary } from 'modules/workflows/lib/workflowValidation'

import { generatePreview } from './helpers/generatePreview'
import { getCurrencies } from './helpers/getCurrencies'
import { getFeatureFlags } from './helpers/getFeatureFlags'
import { getWorkflow } from './helpers/getWorkflow'
import { useFormState } from './helpers/useFormState'

type ModalType = 'publish' | 'save' | 'preview' | null

const REFETCH_QUERIES = [RequestTypeDetailDocument, FindWorkflowDocument, LoadFormDocument]

function RequestTypeDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const { employee, company } = useCurrentSession()
  const [activeModal, setActiveModal] = useState<ModalType>(null)

  /* Mutations */
  const [saveProcurementType, { loading: isSaving }] = useMutation(SaveProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.saveProcurementType,
    refetchQueries: REFETCH_QUERIES,
  })

  const [publishProcurementType, { loading: isPublishing }] = useMutation(
    ProcurementTypePublishDocument,
    {
      extractorFn: (data) => data.procurementMutations.publishProcurementType,
      refetchQueries: REFETCH_QUERIES,
    }
  )

  const [publishChangesProcurementType, { loading: isPublishingChanges }] = useMutation(
    ProcurementTypePublishChangesDocument,
    {
      extractorFn: (data) => data.procurementMutations.publishChangesProcurementType,
      refetchQueries: REFETCH_QUERIES,
    }
  )

  /* Queries */

  // Get the data for the entire RequestType
  const {
    data: requestTypeDetailData,
    loading,
    error,
  } = useQuery(RequestTypeDetailDocument, {
    variables: {
      requestTypeId: Number(id),
      companyId: company.id,
    },
    skip: !id || !company?.id,
  })

  // Feature flags check
  const { isProcurementV2FeatureEnabled } = getFeatureFlags(requestTypeDetailData)

  // Define the request type to be used in the page and dependencies
  const requestType = requestTypeDetailData?.procurement?.typesConnection?.nodes?.[0]
  const isTypePublished = requestType?.enabled ?? false
  const workflowIdFromRequestType = requestType?.workflowsConnection?.nodes?.[0]?.id

  const { data: newRequestPageData } = useQuery(NewRequestPageDocument, {
    variables: { companyId: company.id, enabled: true },
    skip: !isProcurementV2FeatureEnabled || !company?.id || activeModal !== 'preview',
  })

  // Get the workflow data for the request type
  const { data: workflowsData } = useQuery(FindWorkflowDocument, {
    variables: {
      companyId: company.id,
      workflowId: workflowIdFromRequestType ?? 0,
    },
    skip: !isProcurementV2FeatureEnabled || !company?.id || !workflowIdFromRequestType,
  })

  const { navigation } = useNavigation()

  /* Data hooks (form and workflow) */
  // Workflow data
  const {
    isDirty,
    getSerializedNodes,
    getSerializedEdges,
    validateWorkflow: validateCurrentWorkflow,
  } = useWorkflow()
  const { workflow, hasWorkflowChanges, isLastWorkflowVersionPublished } = getWorkflow(
    workflowsData,
    isDirty
  )

  // Form data
  const { form: hookForm } = useLoadForm({
    formId: requestType?.formsConnection?.nodes?.[0]?.id,
    resourceType: 'Procurement::Type',
  })
  const { form, setForm, hasFormChanges, isLastFormVersionPublished } = useFormState(
    requestType,
    hookForm
  )

  // Currencies data
  const { currencies, userCurrency } = getCurrencies(newRequestPageData)

  const hasLocalChanges = hasFormChanges || hasWorkflowChanges
  const hasDraftChanges = !isLastFormVersionPublished || !isLastWorkflowVersionPublished

  /* Handlers */
  const handleSave = async () => {
    if (!id) {
      setActiveModal(null)
      app.setFlash({ type: 'wrong', message: i18n.t('procurement.types.save.fail') })
      return
    }

    // Validate workflow before saving if there are workflow changes
    if (workflow && hasWorkflowChanges) {
      const validationResult = validateCurrentWorkflow()

      if (!validationResult.isValid) {
        // Close the save modal
        setActiveModal(null)

        // Navigate to workflow tab
        navigateToWorkflowTab()

        // Show validation error message
        const errorSummary = getValidationErrorSummary(validationResult.errors)
        app.setFlash({
          type: 'wrong',
          message: errorSummary || i18n.t('procurement.types.save.workflow_validation_failed'),
          time: 5000,
        })
        return
      }
    }

    const formData =
      form && hasFormChanges
        ? { id: Number(form.id), elements: form.elements.map((e) => e.toBackend()) }
        : undefined

    const workflowData =
      workflow && hasWorkflowChanges
        ? { id: workflow.id, edges: getSerializedEdges(), nodes: getSerializedNodes() }
        : undefined

    const action = await saveProcurementType({
      variables: {
        id: Number(id),
        authorId: employee.id,
        companyId: company.id,
        form: formData,
        workflow: workflowData,
      },
    })

    const result = handleResult(action, () => {
      // Close modal on error
      setActiveModal(null)
      app.setFlash({ type: 'wrong', message: i18n.t('procurement.types.save.fail') })
    })

    if (result?.success) {
      setActiveModal(null)
      app.setFlash({ type: 'correct', message: i18n.t('procurement.types.save.success') })
    }
  }

  // Helper to navigate to workflow tab
  const navigateToWorkflowTab = () => {
    const workflowPath = navigation.settings.procurement.requestType.id.fullPath({
      id: id || '',
    })
    window.history.pushState({}, '', `${workflowPath}?tab=workflows`)
    // Force re-render by updating URL
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  const handlePublish = async () => {
    const isPublishChangesFlow = isTypePublished && workflow?.id

    if (!id || !form) {
      setActiveModal(null)
      app.setFlash({
        type: 'wrong',
        message: isPublishChangesFlow
          ? i18n.t('procurement.types.publish_changes.fail')
          : i18n.t('procurement.types.publish.fail'),
      })
      return
    }

    // Validate workflow before publishing if there is a workflow
    if (workflow) {
      const validationResult = validateCurrentWorkflow()

      if (!validationResult.isValid) {
        // Close the publish modal
        setActiveModal(null)

        // Navigate to workflow tab
        navigateToWorkflowTab()

        // Show validation error message
        const errorSummary = getValidationErrorSummary(validationResult.errors)
        app.setFlash({
          type: 'wrong',
          message: errorSummary || i18n.t('procurement.types.publish.workflow_validation_failed'),
          time: 5000,
        })
        return
      }
    }

    const typeId = Number(id)
    const formId = Number(form.id)

    let action: UnhandledResult<Result<unknown>>

    // If type is already published and workflow exists, publish changes
    if (isPublishChangesFlow) {
      action = await publishChangesProcurementType({
        variables: {
          companyId: company.id,
          procurementTypeId: typeId,
          formId: formId,
          workflowId: workflow.id,
        },
      })
    } else {
      // Otherwise, do initial publish (creates workflow if needed)
      action = await publishProcurementType({
        variables: {
          companyId: company.id,
          procurementTypeId: typeId,
          formId: formId,
          workflowId: workflow?.id,
        },
      })
    }

    const result = handleResult(action, (errors) => {
      // Close modal on error
      setActiveModal(null)

      // Show structured errors (like workflow validation errors) as flash messages
      if (Object.keys(errors).length) {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: errorMessages,
        })
      }
    })

    if (result.success) {
      setActiveModal(null)
      app.setFlash({
        type: 'correct',
        message: isPublishChangesFlow
          ? i18n.t('procurement.types.publish_changes.success')
          : i18n.t('procurement.types.publish.success'),
      })
    }
  }

  return (
    <ResponsivePage.Body
      authorized={isProcurementV2FeatureEnabled}
      error={error}
      loading={loading}
      showHeader={false}
      variant="fullscreen"
    >
      {/* Modals */}
      {activeModal === 'preview' && requestType && form && (
        <NewPurchaseRequestModal
          currencies={currencies}
          defaultCurrency={userCurrency}
          hasCustomFormsEnabled
          isPreviewMode
          onClose={() => setActiveModal(null)}
          procurementType={generatePreview(requestType, form)}
        />
      )}
      {activeModal === 'publish' && (
        <PublishTypeModal
          onClose={() => setActiveModal(null)}
          onPublish={handlePublish}
          onPublishLoading={isPublishing || isPublishingChanges}
          publishingForm={!isLastFormVersionPublished}
          publishingWorkflow={!isLastWorkflowVersionPublished}
        />
      )}
      {activeModal === 'save' && (
        <SaveRequestTypeModal
          onClose={() => setActiveModal(null)}
          onSave={handleSave}
          onSaveLoading={isSaving}
          savingForm={hasFormChanges}
          savingWorkflow={hasWorkflowChanges}
        />
      )}

      {/* Header */}
      {requestType && (
        <RequestTypeDetailHeader
          hasDraftChanges={hasDraftChanges}
          hasLocalChanges={hasLocalChanges}
          isPublished={isTypePublished}
          onPreviewForm={() => setActiveModal('preview')}
          onPublish={() => setActiveModal('publish')}
          onSave={() => setActiveModal('save')}
          title={requestType.name}
        />
      )}

      {/* Tab content */}
      <TabContent
        form={form}
        id={id}
        requestType={requestType}
        setForm={setForm}
        workflow={workflow}
      />
    </ResponsivePage.Body>
  )
}

// Container component that provides WorkflowProvider context
const RequestTypeDetailPage = () => {
  return (
    <WorkflowProvider>
      <RequestTypeDetailPageContent />
    </WorkflowProvider>
  )
}

export default RequestTypeDetailPage
